import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { AppStore } from "../app/store";
import { ActionType } from "../@types";
import { Button, Dialog } from "@cmsgov/ds-medicare-gov";
import { hasLoginSession } from "../helpers/loginHelpers";
import { differenceInMilliseconds } from "date-fns";
import { sendKeepAlive } from "../api";
import { withRouter, RouteComponentProps } from "react-router";
import {
  debugUserActivityMonitor,
  logMinutesFromMs,
  logout,
  useTranslate,
} from "../helpers";
import {
  USER_INACTIVITY_TIMEOUT_IN_MS,
  USER_LOGOUT_TIMEOUT_IN_MS,
} from "../config";
import { FormattedMessage } from "react-intl-phraseapp";
import { ApiResponseError } from "../helpers/errors";
import routes from "../app/routes";

const UserActivityMonitor: FunctionComponent<RouteComponentProps> = props => {
  const {
    state: { beneficiary, lastActivity },
    dispatch,
  } = useContext(AppStore.AppContext);

  const loggedIn = hasLoginSession();

  const [showInactiveDialog, setShowInactiveDialog] = useState(false);
  const [inactivityTimeout, setInactivityTimeout] = useState<
    NodeJS.Timeout | undefined
  >(undefined);
  const [logoutTimeout, setLogoutTimeout] = useState<
    NodeJS.Timeout | undefined
  >(undefined);
  const [sendingKeepAlive, setSendingKeepAlive] = useState(false);

  const t = useTranslate();

  const lastActivityRef = useRef(lastActivity);
  const loggedInRef = useRef(loggedIn);

  const activityEvents = ["click", "mousemove", "keydown", "wheel", "scroll"];

  const inactiveTimeoutInMs = USER_INACTIVITY_TIMEOUT_IN_MS;
  const logoutTimeoutInMs = USER_LOGOUT_TIMEOUT_IN_MS;
  const timeToResetLastActivity = 60 * 1000;
  const inactiveTimeoutMins = logMinutesFromMs(inactiveTimeoutInMs);

  const getSinceLastActivity = () =>
    lastActivityRef.current
      ? differenceInMilliseconds(new Date(), new Date(lastActivityRef.current))
      : 0;

  const onLogout = () => setShowInactiveDialog(false);

  const keepAlive = async (): Promise<void> => {
    debugUserActivityMonitor(
      `keepAlive called, current values - loggedInRef: ${loggedInRef.current}`
    );
    if (loggedInRef.current) {
      debugUserActivityMonitor(`sendingKeepAlive set to true`);
      setSendingKeepAlive(true);

      try {
        debugUserActivityMonitor(`sendKeepAlive endpoint called`);
        await sendKeepAlive();
      } catch (_e) {
        const e = _e as Error | ApiResponseError;
        debugUserActivityMonitor(`sendKeepAlive returned error ${e?.message}`);
        if (e instanceof ApiResponseError) {
          const { info } = e;
          const { apiResponseStatus } = info;
          debugUserActivityMonitor(
            `sendKeepAlive returned ApiResponseError with info ${JSON.stringify(
              info,
              undefined,
              2
            )} and apiResopnseStatus ${apiResponseStatus}`
          );

          if (apiResponseStatus == 401) {
            debugUserActivityMonitor(
              `sendKeepAlive returned apiResponseStatus ${apiResponseStatus} and logout is being called`
            );
            logout({
              apiExpired: true, // MCTAPI session already expired
              clientExpired: true, // Session expired message
              dispatch,
              onLogout,
              debugFn: debugUserActivityMonitor,
            });
          }
        }
      }

      // @TODO - Evaluate whether this should be
      // A) Inside a `finally` block (must confirm our build shims Promise.finally)
      // B) Inside both the `try` and `catch` blocks
      // As it is, I think this is called potentially before the async code is run to
      // completion (successfully or erroring), and don't know if that could cause bugs
      debugUserActivityMonitor(`sendingKeepAlive set to false`);
      setSendingKeepAlive(false);
    }
  };

  /**
   * Resets a user's last activity based on a listener detecting one of several events
   * Detects whether it's been at least a minute since the last reset
   * @see activityEvents
   */
  const handleActivity = (): void => {
    const curr = new Date();
    const sinceLastActivity = getSinceLastActivity();

    if (
      !lastActivityRef.current ||
      (lastActivityRef.current && sinceLastActivity >= timeToResetLastActivity)
    ) {
      debugUserActivityMonitor(
        `Planning to update lastActivity if sendingKeepAlive isn't set to true - sendingKeepAlive: ${sendingKeepAlive}, sinceLastActivity: ${logMinutesFromMs(
          sinceLastActivity
        )}`
      );
      if (!sendingKeepAlive) {
        debugUserActivityMonitor(
          `calling keepAlive and dispatching UPDATE_LAST_ACTIVITY with payload: ${curr} (${curr.toLocaleString()})`
        );
        dispatch({ type: ActionType.UPDATE_LAST_ACTIVITY, payload: curr });

        keepAlive();
      }
    }
  };

  const handleNoActivity = (): void => {
    debugUserActivityMonitor(
      `handleNoActivity called, setting showInactiveDialog to true, logout timeout set to ${logMinutesFromMs(
        logoutTimeoutInMs
      )}, sinceLastActivity: ${logMinutesFromMs(getSinceLastActivity())}`
    );
    setShowInactiveDialog(true);

    setLogoutTimeout(
      setTimeout(
        () =>
          logout({
            // no call has been made to the BE since (inactivityTimeout + logoutTimeout)
            apiExpired: false,
            // session being timed out at (inactivityTimeout + logoutTimeout)
            clientExpired: true,
            dispatch,
            onLogout,
            debugFn: debugUserActivityMonitor,
          }),
        logoutTimeoutInMs
      )
    );
  };

  // Every time there is activity, we want to reset the activity timer
  useEffect(() => {
    lastActivityRef.current = lastActivity;

    if (loggedIn && !showInactiveDialog) {
      if (inactivityTimeout) {
        clearTimeout(inactivityTimeout);
      }
      debugUserActivityMonitor(
        `inactivityTimeout passed setTimeout to handleNoActivity in ${inactiveTimeoutMins}`
      );
      setInactivityTimeout(setTimeout(handleNoActivity, inactiveTimeoutInMs));
    }
  }, [lastActivity]);

  const clearTimeouts = (): void => {
    if (inactivityTimeout) {
      debugUserActivityMonitor(`inactivityTimeout cleared (not reset)`);
      clearTimeout(inactivityTimeout);
      setInactivityTimeout(undefined);
    }

    if (logoutTimeout) {
      debugUserActivityMonitor(`logoutTimeout cleared (not reset)`);
      clearTimeout(logoutTimeout);
      setLogoutTimeout(undefined);
    }
  };

  /**
   * This method handles the case where a user still has an active session,
   * but they've been away from MCT for longer than the inactive timeout.
   * This is called when they access MCT again and we will log them out.
   * @TODO - Determine whether there are any logic flaws here
   * This appears to only be called on component initial load
   */
  const handleStaleSession = async (): Promise<void> => {
    const doNotRetrieveMissingBeneRoutes = [
      routes.csrLanding,
      routes.mbpLandingPage,
      routes.slsCallback,
    ];
    const doNotRetrieveRoute = doNotRetrieveMissingBeneRoutes.includes(
      props.location.pathname
    );
    const sinceLastActivity = lastActivity
      ? differenceInMilliseconds(new Date(), new Date(lastActivity))
      : 0;

    debugUserActivityMonitor(
      `handleStaleSession called with the following values - loggedIn: ${loggedIn}, pathname: ${
        props.location.pathname
      }, lastActivity: ${
        lastActivity ? lastActivity.toLocaleString() : ""
      }, sinceLastActivity: ${logMinutesFromMs(
        sinceLastActivity
      )}, inactiveTimeout: ${inactiveTimeoutMins}, doNotRetreiveRoute: ${doNotRetrieveRoute}, beneficiary: ${Boolean(
        beneficiary
      )}`
    );

    if (loggedIn && !beneficiary && !doNotRetrieveRoute) {
      const loginCallbackRoute = {
        pathname: `${routes.slsCallback}`,
        state: { from: `${props.location.pathname}${props.location.search}` },
      };
      debugUserActivityMonitor(
        `handleStaleSession pushing history: ${JSON.stringify(
          loginCallbackRoute,
          undefined,
          2
        )}`
      );
      props.history.push(loginCallbackRoute);
    } else {
      if (!loggedIn && beneficiary) {
        debugUserActivityMonitor(`handleStaleSession dispatching RESET_STATE`);
        dispatch({ type: ActionType.RESET_STATE });
      }
      if (lastActivity && loggedIn && beneficiary) {
        // @TODO - Examine why we're actively logging out here at > inactivityTimout
        // (not considering logoutTimeout)
        if (sinceLastActivity >= inactiveTimeoutInMs) {
          debugUserActivityMonitor(`handleStaleSession logging out`);
          logout({
            // no call has been made to the BE since (inactivityTimeout)
            apiExpired: false,
            // session being timed out at (inactivityTimeout)
            clientExpired: true,
            dispatch,
            onLogout,
            debugFn: debugUserActivityMonitor,
          });
        }
      }
    }
  };

  useEffect(() => {
    handleStaleSession();

    // We always want to record the last activity even if the user isn't logged in
    activityEvents.forEach(eventType =>
      window.addEventListener(eventType, handleActivity)
    );

    return (): void => {
      activityEvents.forEach(eventType =>
        window.removeEventListener(eventType, handleActivity)
      );
    };
  }, []);

  useEffect(() => {
    const hasSessionWithBeneficiary = loggedIn && !!beneficiary;
    loggedInRef.current = hasSessionWithBeneficiary;

    // When the user logs in, start recording activity
    if (hasSessionWithBeneficiary) {
      debugUserActivityMonitor(
        `Bene logged in, keepAlive called, setInactivityTimeout passed setTimeout to trigger handleNoActivity in ${inactiveTimeoutMins}. `
      );
      setInactivityTimeout(setTimeout(handleNoActivity, inactiveTimeoutInMs));
      keepAlive();
    } else {
      debugUserActivityMonitor(
        `bene or loggedIn status changed, clearTimeouts called - loggedIn: ${loggedIn}, bene: ${Boolean(
          beneficiary
        )}`
      );
      clearTimeouts();
    }
  }, [loggedIn, beneficiary]);

  const continueSession = (): void => {
    setShowInactiveDialog(false);

    keepAlive();

    clearTimeouts();
  };

  return (
    <>
      {showInactiveDialog && (
        <Dialog
          getApplicationNode={(): HTMLElement | null =>
            document.getElementById("App")
          }
          escapeExits={false}
          className="UserActivityMonitor__modal"
          heading={t("user_activity.title")}
          onExit={continueSession}
          actions={[
            <Button variation="primary" onClick={continueSession} key="primary">
              {t("user_activity.continue_session")}
            </Button>,
            <Button
              variation="transparent"
              key="cancel"
              onClick={(): Promise<void> =>
                // apiExpired and clientExpired both default to `false`
                // This actively logs out MCTAPI and MBP (is a user logout)
                logout({
                  dispatch,
                  onLogout,
                  debugFn: debugUserActivityMonitor,
                })
              }
            >
              {t("user_activity.logout")}
            </Button>,
          ]}
        >
          <FormattedMessage
            id="user_activity.inactive_message"
            values={{
              inactivityMinutes: Math.floor(inactiveTimeoutInMs / 1000 / 60),
              logoutMinutes: Math.floor(logoutTimeoutInMs / 1000 / 60),
            }}
          />
        </Dialog>
      )}
      {props.children}
    </>
  );
};

export default withRouter(UserActivityMonitor);
